import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Select, Input, Space, Tag } from "antd";
import { PlusOutlined, EditOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import { regionName, regionOptions } from "../../helper/regions";
import { Avatar } from "./employeeUi";

const Employees = () => {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ department: [], office: [], region: [], status: [] }); // each filter takes several values
  const REGIONS = useMemo(() => regionOptions(), []);

  const fetchEmployees = () => {
    request({
      url: `${apiPath.employees}?limit=500&search=${encodeURIComponent(search)}`,
      method: "GET",
      onSuccess: (res) => setEmployees(res?.data?.employees || []),
    });
  };

  useEffect(() => {
    fetchEmployees();
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
    request({ url: apiPath.offices, method: "GET", onSuccess: (res) => setOffices(res?.data?.data || []) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = employees.filter(
    (e) =>
      (!filters.department.length || filters.department.includes(e.department?._id)) &&
      (!filters.office.length || filters.office.includes(e.assignedOffice?._id)) &&
      (!filters.region.length || filters.region.includes(e.region)) &&
      (!filters.status.length || filters.status.includes(e.employmentStatus))
  );
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v || [] }));
  // shared look of the multi-select filters: tags collapse into "+N" when there are many
  const multi = { mode: "multiple", allowClear: true, maxTagCount: 0, maxTagPlaceholder: (omitted) => `${omitted.length} selected`, showArrow: true };
  const labelFor = (key, value) => {
    if (key === "department") return departments.find((d) => d._id === value)?.name || value;
    if (key === "office") return offices.find((o) => o._id === value)?.name || value;
    if (key === "region") return regionName(value);
    return value === "active" ? "Active" : "Inactive";
  };
  const FILTER_TITLES = { department: "Department", office: "Office", region: "Region", status: "Status" };
  const applied = Object.keys(FILTER_TITLES).flatMap((key) => filters[key].map((value) => ({ key, value })));
  const clearAll = () => setFilters({ department: [], office: [], region: [], status: [] });

  const columns = [
    {
      title: "Employee",
      dataIndex: "name",
      width: "25%",
      render: (v, r) => (
        <Link to={`/employees/${r._id}`} className="emp-name-link" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={v} />
          <div>
            <div className="emp-name-link__name" style={{ fontWeight: 600 }}>{v}</div>
            <div style={{ color: "#6B7280", fontSize: 12 }}>
              {r.employeeId} · {r.email}
            </div>
          </div>
        </Link>
      ),
    },
    { title: "Department", dataIndex: ["department", "name"], width: "15%", render: (v) => v || "—" },
    { title: "Role", dataIndex: ["role", "name"], width: "14%", render: (v) => v || "—" },
    { title: "Office", dataIndex: ["assignedOffice", "name"], width: "11%", render: (v) => v || "—" },
    { title: "Region", dataIndex: "region", width: "11%", render: (v) => (v ? regionName(v) : "—") },
    {
      title: "Actions",
      align: "center",
      width: "14%",
      render: (_, record) => (
        <Space size={8}>
          <Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/employees/${record._id}`)}>
            View
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/employees/${record._id}/edit`)}>
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader
        title="Employees"
        subtitle="Manage employee records, departments, roles and assignments"
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/employees/new")}>
            Add Employee
          </Button>
        }
      />
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          allowClear
          placeholder="Search name, email or ID"
          prefix={<SearchOutlined />}
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={fetchEmployees}
        />
        <Select {...multi} placeholder="All departments" style={{ width: 230 }} value={filters.department} onChange={set("department")} options={departments.map((d) => ({ value: d._id, label: d.name }))} />
        <Select {...multi} placeholder="All offices" style={{ width: 210 }} value={filters.office} onChange={set("office")} options={offices.map((o) => ({ value: o._id, label: o.name }))} />
        <Select {...multi} showSearch optionFilterProp="label" placeholder="All regions" style={{ width: 230 }} value={filters.region} onChange={set("region")} options={REGIONS} />
        <Select {...multi} placeholder="Any status" style={{ width: 170 }} value={filters.status} onChange={set("status")} options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} />
      </Space>
      {applied.length ? (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 14 }}>
          <span style={{ color: "#6B7280", fontSize: 12, marginRight: 4 }}>Showing:</span>
          {applied.map(({ key, value }) => (
            <Tag
              key={`${key}-${value}`}
              closable
              onClose={() => setFilters((f) => ({ ...f, [key]: f[key].filter((v) => v !== value) }))}
              style={{ margin: 0, padding: "3px 10px", borderRadius: 14, background: "#EEF2FF", borderColor: "#C7D2FE", color: "#3730A3" }}
            >
              {FILTER_TITLES[key]}: <b>{labelFor(key, value)}</b>
            </Tag>
          ))}
          <Button type="link" size="small" onClick={clearAll}>
            Clear all
          </Button>
          <span style={{ color: "#6B7280", fontSize: 12, marginLeft: "auto" }}>{rows.length} of {employees.length} employees</span>
        </div>
      ) : null}
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 15 }}
        tableLayout="fixed"
        locale={{ emptyText: <EmptyState message="No employees found" actionLabel="Add Employee" onAction={() => navigate("/employees/new")} /> }}
      />
    </PageTransition>
  );
};

export default Employees;
